import { exportClauses } from "@/api";
import { Button } from "@/components/shared/buttons/Button";
import { ArrowDropDown, SaveAlt } from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

/**
 * ClausesExportButton component
 */
export const ClausesExportButton = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const { mutate, isPending } = useMutation<Blob, Error, "csv" | "xlsx">({
        mutationFn: (format) => exportClauses(format),
        onSuccess: (blob, format) => {
            downloadFile(blob, format);
            enqueueSnackbar("Clauses exported successfully", {
                variant: "success",
            });
            handleClose();
        },
        onError: () => {
            enqueueSnackbar("An error occurred while exporting the clauses", {
                variant: "error",
            });
            handleClose();
        },
    });

    const downloadFile = (blob: Blob, format: "csv" | "xlsx") => {
        const mimeType =
            format === "csv"
                ? "text/csv"
                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const fileExtension = format;
        const url = window.URL.createObjectURL(
            new Blob([blob], { type: mimeType })
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `clauses_export.${fileExtension}`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleExport = (format: "csv" | "xlsx") => {
        mutate(format);
    };

    return (
        <>
            <Button
                type="button"
                variant="outlined"
                disableElevation
                sx={{ borderRadius: 22 }}
                startIcon={<SaveAlt />}
                endIcon={<ArrowDropDown />}
                onClick={handleClick}
                loading={isPending}
                loadingPosition="start"
            >
                {isPending ? "Exporting..." : "Export"}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <MenuItem onClick={() => handleExport("xlsx")}>
                    Export as Excel
                </MenuItem>
                <MenuItem onClick={() => handleExport("csv")}>
                    Export as CSV
                </MenuItem>
            </Menu>
        </>
    );
};
